import React, { createContext, useState, useContext, useEffect } from 'react';
import { AiInsight, AiInsightsFilters } from './AiInsightsApiTypesServices';
import { aiInsightsBridgeCrud } from '../api/CrudInsights';
import { applyFilters, getInitialFilters } from '../utils';

// Types pour les dimensions dynamiques
export type DimensionValue = {
  id: string;
  label: string;
  [key: string]: string | number | undefined;
};
export type AvailableDimensions = {
  [dimension: string]: {
    name: string;
    values: DimensionValue[];
    [key: string]: unknown;
  };
};

type AiInsightsContextType = {
  aiInsights: AiInsight[] | null;
  isLoading: boolean;
  error: string | null;
  refreshAiInsights: () => void;
  firstRender: boolean;
  filters: AiInsightsFilters;
  setFilters: (filters: AiInsightsFilters) => void;
  filteredFindings: AiInsight[];
  chartType: string;
  setChartType: (type: string) => void;
  selectedTenant: null | string;
  setselectedTenant: (tenant: string | null) => void;
  availableDimensions: AvailableDimensions;
};

interface AiInsightsContextProviderProps {
  $http: unknown;
  children?: React.ReactNode;
  $rootScope: unknown;
  $routeParams: unknown;
  $timeout: unknown;
  $location: unknown;
  AuthServices: unknown;
}

const AiInsightsContext = createContext<AiInsightsContextType | undefined>(undefined);
export const useAiInsightsContext = () => {
	const contextValue = useContext(AiInsightsContext);
	if (contextValue === undefined) {
		throw new Error('useAiInsightsContext must be used within an AiInsightsContextProvider');
	}
	return contextValue;
};

export const AiInsightsContextProvider: React.FC<AiInsightsContextProviderProps> = ({ $http, children, $rootScope, $routeParams }) => {
	const [aiInsights, setAiInsights] = useState<AiInsight[] | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const { getAiInsights, getAvailableDimensions } = aiInsightsBridgeCrud($http, $rootScope, $routeParams);
	const [firstRender, setFirstRender] = useState(true);
	const [filters, setFilters] = useState<AiInsightsFilters>(() => getInitialFilters());
	const [filteredFindings, setFilteredFindings] = useState<AiInsight[]>([]);
	const [chartType, setChartType] = useState('line');
	const [availableDimensions, setAvailableDimensions] = useState<AvailableDimensions>({});

	const selectedTenant = filters.tenant || null;

	const setselectedTenant = (tenant: string | null) => {
		setFilters({ ...filters, tenant });
	};

	const fetchAiInsights = () => {
		setIsLoading(true);
		setError(null);
		getAiInsights(
			(data: AiInsight[]) => {
				setAiInsights(data);
				setIsLoading(false);
				setError(null);
				setFirstRender(false);
			},
			(err: string) => {
				setAiInsights(null);
				setError(err);
				setIsLoading(false);
				setFirstRender(false);
			}
		);
	};

	const fetchAvailableDimensions = () => {
		getAvailableDimensions(
			(data: AvailableDimensions) => {
				setAvailableDimensions(data);
			},
			() => {
				setAvailableDimensions({});
			}
		);
	};

	useEffect(() => {
		fetchAiInsights();
		fetchAvailableDimensions();
	}, []);

	useEffect(() => {
		if (aiInsights) {
			setFilteredFindings(applyFilters(aiInsights, filters));
		}
	}, [aiInsights, filters]);


	const didSyncTenant = React.useRef(false);
	useEffect(() => {
		if (didSyncTenant.current) return;
		const tenantValues: DimensionValue[] = availableDimensions?.Tenant?.values || [];
		const params = new URLSearchParams(window.location.search);
		const urlTenant = params.get('tenant');
		const tenantIds = tenantValues.map(t => t.id);
		const currentTenant = filters.tenant ?? '';
		const urlTenantStr = urlTenant ?? '';
		if (
			urlTenantStr &&
			tenantIds.includes(urlTenantStr) &&
			currentTenant !== urlTenantStr
		) {
			setFilters(prev => ({ ...prev, tenant: urlTenantStr }));
			didSyncTenant.current = true;
		}
		if (!urlTenantStr || currentTenant === urlTenantStr) {
			didSyncTenant.current = true;
		}
	}, [availableDimensions]);

	useEffect(() => {
		const tenantValues: DimensionValue[] = availableDimensions?.Tenant?.values || [];
		if (!tenantValues || tenantValues.length === 0) {
			return;
		}
		const tenantIds = tenantValues.map(t => t.id);
		const params = new URLSearchParams(window.location.search);
		const urlTenant = params.get('tenant');
		const storage = localStorage.getItem('aiInsightsFilters');
		let storageTenant = null;
		if (storage) {
			try {
				storageTenant = JSON.parse(storage).tenant;
			} catch {}
		}
		const currentTenant = filters.tenant ?? '';

		const isValid = (val: string | null | undefined) => val && tenantIds.includes(val);
		const mustReset =
			(currentTenant && !isValid(currentTenant)) ||
			(urlTenant && !isValid(urlTenant)) ||
			(storageTenant && !isValid(storageTenant));
		if (mustReset && currentTenant !== null) {
			setFilters(prev => ({ ...prev, tenant: null }));
			params.delete('tenant');
			window.history.replaceState(null, '', window.location.pathname + (params.toString() ? '?' + params.toString() : ''));
			const storageObj = storage ? { ...JSON.parse(storage), tenant: null } : { tenant: null };
			localStorage.setItem('aiInsightsFilters', JSON.stringify(storageObj));
		}
	}, [availableDimensions, filters.tenant]);

	const refreshAiInsights = () => {
		fetchAiInsights();
		fetchAvailableDimensions();
	};

	return (
		<AiInsightsContext.Provider
			value={{
				aiInsights,
				isLoading,
				error,
				refreshAiInsights,
				firstRender,
				filters,
				setFilters,
				filteredFindings,
				chartType,
				setChartType,
				selectedTenant,
				setselectedTenant,
				availableDimensions
			}}
		>
			{children}
		</AiInsightsContext.Provider>
	);
};

export default AiInsightsContextProvider;
